<template>
  <SoDialog
    v-model="value"
    :card-title="modalTitle"
    :max-width="480"
    @on-close="resetModal()"
  >
    <template v-if="!submissionSuccess">
      <SoSelect
        v-model="meterTypeSelection"
        :items="meterTypeOptions"
        label="Meter type"
        placeholder="Select type of meter"
        class="mb-3"
      />
      <SoSelect
        v-model="requestReasonSelection"
        :items="requestReasonOptions"
        label="Reason for request"
        placeholder="Select a reason"
        class="mb-3"
      />
      <SoSelect
        v-model="teamSelection"
        :items="teamOptions"
        label="Assign to team"
        placeholder="Select a team"
        class="mb-3"
      />
      <SoAlert v-if="submissionError" class="mt-5" type="error">
        {{ errorMessage }}
      </SoAlert>
      <div class="d-flex gap-2 justify-end mt-9">
        <SoButton
          title="Cancel"
          type="secondary"
          large
          @click="value = false"
        />
        <SoButton
          title="Request Photos"
          :disabled="!formIsValid"
          :loading="isLoading"
          large
          type="primary"
          @click="submitRequest"
        />
      </div>
    </template>
    <template v-else>
      <div class="title-text mt-n4">
        <v-icon icon="check-circle success-icon mr-1" size="x-large"></v-icon>
        <h4 class="d-inline success-title">Photo Request Successful</h4>
      </div>
      <p class="mt-4 mb-7 success-text">
        The ticket will be created within the next few minutes. Refresh the page
        to check the status.
      </p>
      <p class="mt-3 success-text">Meter type: {{ meterTypeSelection }}</p>
      <p class="mt-3 success-text">Reason: {{ requestReasonSelection }}</p>
      <p class="mt-3 success-text">Team assigned: {{ teamSelectionName }}</p>
      <SoButton
        title="Close"
        type="primary"
        class="mt-8"
        large
        full-width
        @click="resetModal()"
      />
    </template>
  </SoDialog>
</template>

<script lang="ts">
  import { defineComponent } from "vue"
  import SoDialog from "@/components/SoDialog.vue"
  import SoButton from "@/components/formControls/SoButton.vue"
  import SoSelect from "@/components/formControls/SoSelect.vue"
  import SoAlert from "@/components/SoAlert.vue"
  import {
    REQUEST_REASON_OPTIONS,
    FRESHDESK_GROUP_OPTIONS,
  } from "@/constants/outboundPhotoOptions"
  import { MeterReadingsMutator } from "@/mutators/meterReadings/MeterReadingsMutator"
  import {
    OutboundPhotoResponse,
    ReadingFuelEnum,
  } from "@/api/backofficeRest/generated/model"
  import { SoSelectObjectItem } from "@/types/components/SoSelect"
  import { useCurrentAccountStore } from "@/stores/currentAccount"
  import { errorHandler, errorReporter } from "@/services/ErrorService"
  import { legacyFreshdeskGroupNameMap } from "@/models/freshdeskTicket/freshdeskHelper"

  export default defineComponent({
    components: {
      SoDialog,
      SoButton,
      SoSelect,
      SoAlert,
    },
    props: {
      modelValue: {
        type: Boolean,
        default: false,
        required: true,
      },
    },
    emits: ["update:modelValue"],
    data() {
      return {
        isLoading: false,
        meterTypeSelection: null,
        requestReasonSelection: null,
        teamSelection: null,
        requestReasonOptions: REQUEST_REASON_OPTIONS,
        createOutboundPhotoRequest:
          MeterReadingsMutator.createOutboundPhotoRequest(),
        submissionError: false,
        submissionSuccess: false,
        errorMessage: "",
        defaultErrorMessage: "There was an error with processing your request.",
      }
    },
    computed: {
      value: {
        get() {
          return this.modelValue
        },
        set(value: boolean) {
          this.$emit("update:modelValue", value)
        },
      },
      teamOptions(): SoSelectObjectItem[] {
        return FRESHDESK_GROUP_OPTIONS.filter(
          (key) => key in legacyFreshdeskGroupNameMap,
        ).map((key) => ({
          title: legacyFreshdeskGroupNameMap[key],
          value: key,
        }))
      },
      meterTypeOptions(): SoSelectObjectItem[] {
        // generate options from the enum, so we always use a valid value
        return Object.keys(ReadingFuelEnum).map((fuel) => {
          return {
            value: ReadingFuelEnum[fuel],
            title: fuel,
          }
        })
      },
      formIsValid() {
        return (
          !!this.meterTypeSelection &&
          !!this.requestReasonSelection &&
          !!this.teamSelection
        )
      },
      modalTitle() {
        return this.submissionSuccess ? "" : "Request New Meter Photo(s)"
      },
      teamSelectionName() {
        return legacyFreshdeskGroupNameMap[this.teamSelection ?? ""] ?? ""
      },
    },
    methods: {
      submitRequest() {
        this.isLoading = true
        this.submissionError = false
        this.submissionSuccess = false

        if (
          this.meterTypeSelection &&
          this.requestReasonSelection &&
          this.teamSelection
        ) {
          this.createOutboundPhotoRequest
            .mutateAsync({
              accountNumber: useCurrentAccountStore().accountNumber,
              body: {
                meterPointType: this.meterTypeSelection,
                requestReason: this.requestReasonSelection,
                freshdeskGroupId: this.teamSelection,
                freshdeskGroupName: this.teamSelectionName,
              },
            })
            .then((response) => {
              this.isLoading = false
              const responseMessage = (response as OutboundPhotoResponse)
                ?.message

              if (responseMessage === "success") {
                this.submissionSuccess = true
              } else {
                this.handleSubmissionError(responseMessage)
              }
            })
            .catch((error) => {
              this.handleSubmissionError(error)
            })
        } else {
          this.handleSubmissionError("Please select all fields")
        }
      },
      handleSubmissionError(error) {
        if (typeof error === "string") {
          this.errorMessage = error
        } else if (error instanceof Error) {
          this.errorMessage = errorHandler.userMessage(
            error,
            "Failed to create the photo request",
          )
        } else {
          this.errorMessage = ""
        }
        if (!this.errorMessage) {
          // error not found
          this.errorMessage = this.defaultErrorMessage
        }

        errorReporter.report(this.errorMessage)
        this.submissionError = true
        this.isLoading = false
      },
      resetModal() {
        this.value = false
        this.submissionSuccess = false
      },
    },
  })
</script>

<style scoped>
  .success-icon {
    font-size: 26px;
    line-height: 28px;
    vertical-align: middle;
    color: rgb(var(--v-theme-success));
  }

  .success-title {
    vertical-align: middle;
  }

  .success-text {
    font-size: 18px;
  }
</style>
